import { useCallback, useEffect, useRef } from 'react';
import {
  useEdmDocumentationVersionMarkAsReadMutation,
  useEdmDocumentationVersionViewCreateMutation,
  useEdmDocumentationVersionViewIncrementMutation,
} from '@lib/graphql-common';
import { ErrorLike } from '@apollo/client';
import { isUpgradePlanRequiredError } from '@lib/utils/graphQLErrors';
import {
  sendViewCreateBeacon,
  sendViewIncrementBeacon,
} from './documentationViewBeacon';

/** BE rejects `readingDurationSeconds` below 1. */
const MIN_SECONDS = 1;

/** Caps how many distinct versions' bookkeeping the maps below can hold. */
const MAX_TRACKED_VERSIONS = 100;

// Set once BE refuses a create with `UPGRADE_PLAN_REQUIRED`. The gate is
// per-company — the mutation needs `DOCUMENTATION_MANAGEMENT` in the plan — so
// after one refusal every later create this page load is equally doomed.
// Module-level so all hook instances stop sending, instead of repeating the
// mutation on every viewer close for the whole session.
let upgradePlanRequired = false;

// Module-level, keyed by documentationVersionId, shared by every hook
// instance for the life of the page load — not per-instance `useRef`s. A
// version's view record and its accrued-but-not-yet-credited seconds must
// survive a component remount, or the next open recreates the record instead
// of incrementing it. That remount happens more than it looks: switching
// between a document's History "Activity"/"Versions" sub-tabs, or between
// different DOCUMENT question fields in a task, conditionally unmounts the
// `FileViewerModal` that was tracking the earlier open — same underlying
// cause as a route change, just without an actual navigation. From a user's
// perspective all of these are still one continuous reading session, so the
// record they produce must be too.
const viewIdsByVersion = new Map<string, string>();
// Versions with a create already on the wire, keyed to its eventual result —
// not just a presence flag — so a racing caller (a second flush before the
// first response, or `confirmRead` settling into a passive flush's create)
// can await the same request instead of skipping past it or firing a second
// one.
const inFlightByVersion = new Map<string, Promise<string | null>>();
// PDF/image: total visible-and-open milliseconds per version, across opens
// and remounts.
const activeMsByVersion = new Map<string, number>();
// Video: playback seconds banked by *previous* sessions. The player resets to
// zero on every reopen, so the live reading is added on top of this.
const watchedCarryByVersion = new Map<string, number>();
// Seconds already accepted by BE per version. The difference against the
// accrued total is what is still owed, so a flush that happens while a create
// is in flight needs no queue of its own — the debt stays outstanding.
const creditedSecondsByVersion = new Map<string, number>();
// Start of the wall-clock segment currently accruing (PDF/image only) for a
// version, or absent while paused/closed. Module-level, not a per-instance
// ref: two `FileViewerModal` instances can end up open on the same version at
// once (the main viewer and an AI Assistant doc chip pointing at the same
// document, say). A shared clock, paused/resumed idempotently, measures the
// overlap once instead of once per open instance.
const segmentStartByVersion = new Map<string, number>();
// Which mounted instance's playback counter is trusted for a video version's
// live accrual — set by the first instance to open it, cleared when that
// instance closes. Unlike wall-clock time, two open players have independent
// playback positions with no shared clock to dedupe against, so only one is
// read; a concurrent second tracker does not bank its own overlapping
// progress on top.
const primaryTrackerByVersion = new Map<string, symbol>();
// Recency of the last touch per version, oldest-first (`Map` preserves
// insertion order; touching re-inserts). Bounds the maps above so a
// long-lived session that opens hundreds of distinct documents does not grow
// them without bound — see `touchVersion`.
const lastTouchedAtByVersion = new Map<string, number>();

/**
 * Marks a version as recently active, and evicts the least-recently-touched
 * versions once more than `MAX_TRACKED_VERSIONS` are held. Never evicts a
 * version that is currently open or has a request on the wire — only ones
 * that are both idle and fully settled — so this never interferes with the
 * remount-survival guarantee for a document still in play.
 */
function touchVersion(versionId: string): void {
  lastTouchedAtByVersion.delete(versionId);
  lastTouchedAtByVersion.set(versionId, Date.now());
  const excess = lastTouchedAtByVersion.size - MAX_TRACKED_VERSIONS;
  if (excess <= 0) return;

  const evictable = Array.from(lastTouchedAtByVersion.keys()).filter(
    (candidate) =>
      !primaryTrackerByVersion.has(candidate) &&
      !inFlightByVersion.has(candidate) &&
      !segmentStartByVersion.has(candidate),
  );
  evictable.slice(0, excess).forEach((candidate) => {
    lastTouchedAtByVersion.delete(candidate);
    viewIdsByVersion.delete(candidate);
    activeMsByVersion.delete(candidate);
    watchedCarryByVersion.delete(candidate);
    creditedSecondsByVersion.delete(candidate);
  });
}

/**
 * Awaits an in-flight create for `versionId`, following its retry chain
 * (a beacon failure kicks off a fresh flush, which installs a new promise) —
 * recursive rather than a loop so each step's await has something new to
 * wait on.
 */
async function waitForRacingCreate(
  versionId: string,
  racing: Promise<string | null>,
): Promise<void> {
  await racing.catch(() => null);
  if (viewIdsByVersion.has(versionId)) return;
  const next = inFlightByVersion.get(versionId);
  if (!next || next === racing) return;
  await waitForRacingCreate(versionId, next);
}

/** The module state above would otherwise leak between test cases. */
export function resetDocumentViewTrackingForTests() {
  upgradePlanRequired = false;
  viewIdsByVersion.clear();
  inFlightByVersion.clear();
  activeMsByVersion.clear();
  watchedCarryByVersion.clear();
  creditedSecondsByVersion.clear();
  segmentStartByVersion.clear();
  primaryTrackerByVersion.clear();
  lastTouchedAtByVersion.clear();
}

type Params = {
  /**
   * Version currently on screen; `null` / `undefined` when no viewer is open.
   */
  documentationVersionId?: string | null;
  /**
   * Supply for video documents only. Returns seconds of *actual playback*
   * accrued in the current viewing session. Its presence switches the hook into
   * video mode, where an unplayed document records nothing at all. Read when
   * the session opens rather than when it is reported — see `sessionModeRef`.
   */
  getWatchedSeconds?: (() => number) | null;
};

/**
 * Records a document view whenever a documentation version is opened in a
 * viewer, on any surface in any app. Owned by `FileViewerModal`, which is the
 * one component that renders every document viewer and therefore the only place
 * that knows the file type and can see playback progress.
 *
 * Nothing is sent while the document is open — duration accrues, and is
 * reported when the viewing session ends:
 *
 * | Ends because…                          | Detected via          | Sent with |
 * |----------------------------------------|-----------------------|-----------|
 * | Modal closed, route change, unmount     | effect cleanup        | Apollo    |
 * | Tab hidden, minimised, app switched     | `visibilitychange`    | keepalive |
 * | Tab/browser closed, refresh, navigation | `visibilitychange` + `pagehide` | keepalive |
 *
 * The last two rows are why the hidden/`pagehide` path exists: React cleanup
 * never runs when the page is being torn down. `visibilitychange` to hidden is
 * the last event browsers reliably deliver, with `pagehide` as the
 * Safari-shaped backstop; both are wired to the same flush, which is idempotent
 * so a double fire costs nothing. See `documentationViewBeacon` for why those
 * flushes bypass Apollo.
 *
 * **What counts as duration depends on the file type.** PDFs and images accrue
 * wall-clock time the viewer stayed open, paused while the tab is hidden — a
 * document sitting in a background tab is not being read. Video instead accrues
 * real playback time via `getWatchedSeconds`, so scrubbing, pausing and idling
 * are all excluded. A video opened and closed without ever being played records
 * **nothing** — no create, no increment — because a duration of zero is not a
 * view. PDFs and images are floored at one second instead, so a quick open
 * still registers.
 *
 * The first flush of a version creates the view record with the seconds
 * accrued; later flushes increment it. So one open produces exactly one
 * mutation, and reopening the same document accumulates into the original
 * record rather than inflating the Views history and Analytics counts — even
 * across a component remount (switching a document's History
 * Activity/Versions sub-tabs, moving between DOCUMENT question fields in a
 * task) that tears down and recreates the `FileViewerModal` that was
 * tracking it: the record id and accrued-but-uncredited seconds live at
 * module scope, not in that component's state, so they survive it. Only a
 * genuine new page load starts a version over. A different version (e.g. an
 * older one from History → Versions) gets its own record.
 *
 * Failures are swallowed on purpose. View tracking is a side effect of reading
 * a document, never the point of it, so a rejected mutation must not surface a
 * toast over the viewer the user is looking at. A failed keepalive flush
 * retries once over Apollo — the rejection handler running at all proves the
 * page survived. `UPGRADE_PLAN_REQUIRED` is expected rather than exceptional:
 * the mutation is gated on `DOCUMENTATION_MANAGEMENT`, so it always fails for
 * Free-plan companies — the first refusal latches and stops all tracking for
 * the rest of the page load.
 *
 * Documents attached to a form-template question carry an explicit "I have
 * read this document" CTA on top of this. It does not create a second,
 * competing view record — `confirmRead` (below) settles the session into the
 * same record this hook would create/increment on close, then separately
 * confirms the read via `documentationVersionMarkAsRead`. A view never implies
 * a read on its own; only the explicit confirmation does.
 */
export default function useDocumentationViewTracking({
  documentationVersionId,
  getWatchedSeconds,
}: Params) {
  const [versionViewCreate] = useEdmDocumentationVersionViewCreateMutation();
  const [versionViewIncrement] =
    useEdmDocumentationVersionViewIncrementMutation();
  const [versionMarkAsRead] = useEdmDocumentationVersionMarkAsReadMutation();

  // Mutation identities are stable per Apollo, but keeping them in refs lets
  // the tracking effect depend on `documentationVersionId` alone — otherwise a
  // re-render mid-session would re-run it and restart the viewing session.
  const createRef = useRef(versionViewCreate);
  createRef.current = versionViewCreate;
  const incrementRef = useRef(versionViewIncrement);
  incrementRef.current = versionViewIncrement;
  const markAsReadRef = useRef(versionMarkAsRead);
  markAsReadRef.current = versionMarkAsRead;
  const watchedRef = useRef(getWatchedSeconds);
  watchedRef.current = getWatchedSeconds;
  const isVideo = !!getWatchedSeconds;
  const isVideoRef = useRef(isVideo);
  isVideoRef.current = isVideo;

  // Identifies this hook instance for `primaryTrackerByVersion` — see there.
  const instanceTokenRef = useRef<symbol | undefined>(undefined);
  if (!instanceTokenRef.current) {
    instanceTokenRef.current = Symbol('doc-view-tracker');
  }
  // Whether this instance is the one trusted to read live video playback for
  // the version it currently has open. Set on open; see
  // `primaryTrackerByVersion`.
  const isPrimaryRef = useRef(true);

  // { versionId → how that version's session should be measured }, captured
  // when the session opened. The two values above must NOT be read at report
  // time: the render that ends a session is usually the same render that
  // clears the attachment (`setFileViewerDocument(null)`), so by the time the
  // cleanup runs they already describe "no document open" — which would report
  // a watched video as a one-second wall-clock view. Entries outlive their
  // session so a create resolving after close still settles correctly.
  const sessionModeRef = useRef(
    new Map<string, { isVideo: boolean; getWatched?: (() => number) | null }>(),
  );
  const sessionMode = useCallback(
    (versionId: string) =>
      sessionModeRef.current.get(versionId) ?? {
        isVideo: false,
        getWatched: null,
      },
    [],
  );

  // Version whose viewing session is still open. Once a session ends its
  // playback is banked into the carry, and the live counter must stop being
  // consulted — it keeps reporting that session's total until the player
  // remounts, so a late flush (a create resolving after close) would otherwise
  // count the same seconds twice.
  const activeVersionRef = useRef<string | null>(null);

  // The attachment can arrive after the version id — a viewer opened while its
  // query was still in flight starts out looking like a PDF. Upgrade the open
  // session's mode when it turns out to be video. Only ever an upgrade: the
  // render that ends a session has already cleared the attachment, and must not
  // be allowed to downgrade the captured mode back to wall-clock.
  if (
    isVideo &&
    documentationVersionId &&
    activeVersionRef.current === documentationVersionId
  ) {
    sessionModeRef.current.set(documentationVersionId, {
      isVideo: true,
      getWatched: getWatchedSeconds,
    });
  }

  /** Total seconds read so far for this version, across every open. */
  const accruedSeconds = useCallback(
    (versionId: string) => {
      const { isVideo: sessionIsVideo, getWatched } = sessionMode(versionId);
      if (sessionIsVideo) {
        const carry = watchedCarryByVersion.get(versionId) || 0;
        const isLive =
          activeVersionRef.current === versionId && isPrimaryRef.current;
        return carry + (isLive ? (getWatched?.() ?? 0) : 0);
      }
      return Math.floor((activeMsByVersion.get(versionId) || 0) / 1000);
    },
    [sessionMode],
  );

  /** Folds the open wall-clock segment into the total and stops accruing. */
  const pauseAccrual = useCallback((versionId: string) => {
    const start = segmentStartByVersion.get(versionId);
    if (start === undefined) return;
    segmentStartByVersion.delete(versionId);
    const elapsed = Date.now() - start;
    if (elapsed <= 0) return;
    activeMsByVersion.set(
      versionId,
      (activeMsByVersion.get(versionId) || 0) + elapsed,
    );
  }, []);

  const resumeAccrual = useCallback(
    (versionId: string) => {
      // Video duration comes from playback, which the player tracks itself.
      if (sessionMode(versionId).isVideo) return;
      if (segmentStartByVersion.has(versionId)) return;
      segmentStartByVersion.set(versionId, Date.now());
    },
    [sessionMode],
  );

  /**
   * The two view mutations, called directly over Apollo with no retry policy
   * of their own — `flush` and `confirmRead` each wrap these with their own
   * beacon-first/retry decisions, which differ (see each). Shared here is
   * just the request shape and the `UPGRADE_PLAN_REQUIRED` handling, which
   * must not diverge between the two paths.
   */
  const createOverApollo = useCallback(
    async (versionId: string, seconds: number): Promise<string | null> => {
      try {
        const { data } = await createRef.current({
          variables: {
            documentationVersionId: versionId,
            viewedAt: new Date(Date.now()).toISOString(),
            readingDurationSeconds: seconds,
          },
        });
        return data?.documentationVersionViewCreate?.id ?? null;
      } catch (error) {
        // Free-plan companies cannot record views at all; drop the accrued
        // time so a later open does not try to replay it, and latch so no
        // further session bothers BE this page load.
        if (isUpgradePlanRequiredError(error as ErrorLike)) {
          upgradePlanRequired = true;
          activeMsByVersion.delete(versionId);
          watchedCarryByVersion.delete(versionId);
        }
        throw error;
      }
    },
    [],
  );

  const incrementOverApollo = useCallback(
    (viewId: string, owed: number) =>
      incrementRef
        .current({ variables: { id: viewId, readingDurationSeconds: owed } })
        .then(() => undefined),
    [],
  );

  /**
   * Confirms the read via `documentationVersionMarkAsRead` and throws if BE
   * resolves without a payload — the mutation's return type is nullable, so a
   * successful-looking response can still mean nothing was actually set.
   * `confirmRead` must not report a read as confirmed in that case.
   */
  const confirmMarkAsRead = useCallback(async (versionId: string) => {
    const { data } = await markAsReadRef.current({
      variables: { id: versionId },
    });
    if (!data?.documentationVersionMarkAsRead) {
      throw new Error(
        `documentationVersionMarkAsRead returned no payload for version ${versionId}`,
      );
    }
  }, []);

  /**
   * Reports duration BE has not been told about yet. `useBeacon` picks the
   * keepalive transport for flushes that may be the page's last act.
   */
  const flush = useCallback(
    (versionId: string, useBeacon: boolean) => {
      if (upgradePlanRequired) return;
      const accrued = accruedSeconds(versionId);
      const viewId = viewIdsByVersion.get(versionId);

      if (!viewId) {
        // A create is already on the wire; its completion settles the rest.
        if (inFlightByVersion.has(versionId)) return;
        // A video nobody played is not a view — record nothing at all. PDFs and
        // images are floored at 1 so a quick open still registers.
        if (sessionMode(versionId).isVideo && accrued < MIN_SECONDS) return;
        const seconds = Math.max(MIN_SECONDS, accrued);

        const onCreated = (createdId: string | null) => {
          inFlightByVersion.delete(versionId);
          if (!createdId) return;
          viewIdsByVersion.set(versionId, createdId);
          creditedSecondsByVersion.set(versionId, seconds);
          touchVersion(versionId);
          // Settle anything that accrued while the create was in flight.
          flush(versionId, useBeacon);
        };

        const request: Promise<string | null> = useBeacon
          ? sendViewCreateBeacon(
              versionId,
              new Date(Date.now()).toISOString(),
              seconds,
            )
              .then((id) => {
                onCreated(id);
                return id;
              })
              .catch(() => {
                inFlightByVersion.delete(versionId);
                // Reaching this handler proves the page survived — a
                // torn-down page never runs the rejection — so retry over
                // Apollo rather than drop the view. A fresh flush recomputes
                // whatever accrued since, rather than replaying the stale
                // amount this attempt started with.
                flush(versionId, false);
                return null;
              })
          : createOverApollo(versionId, seconds)
              .then((id) => {
                onCreated(id);
                return id;
              })
              .catch(() => {
                inFlightByVersion.delete(versionId);
                return null;
              });

        inFlightByVersion.set(versionId, request);
        return;
      }

      const credited = creditedSecondsByVersion.get(versionId) || 0;
      const owed = accrued - credited;
      if (owed <= 0) return;
      creditedSecondsByVersion.set(versionId, credited + owed);
      touchVersion(versionId);

      const rollback = () => {
        // Put the seconds back so the next flush retries rather than lose them.
        const current = creditedSecondsByVersion.get(versionId) || 0;
        creditedSecondsByVersion.set(versionId, Math.max(0, current - owed));
      };

      if (useBeacon) {
        sendViewIncrementBeacon(viewId, owed).catch(() => {
          rollback();
          // Same reasoning as the create above: the page survived, and a
          // surface that unmounted meanwhile will never flush again — retry
          // over Apollo now instead of waiting for a next flush.
          flush(versionId, false);
        });
        return;
      }

      incrementOverApollo(viewId, owed).catch(rollback);
    },
    [accruedSeconds, createOverApollo, incrementOverApollo, sessionMode],
  );

  /**
   * Settles the current session's view record — reusing the same one this
   * hook would otherwise create/increment on close, a confirmed read is
   * never a second row — then explicitly confirms the read via
   * `documentationVersionMarkAsRead`, which is the only thing that sets
   * `Documentation.read`. This function has no unplayed-video guard — a
   * sub-1-second video is floored to `MIN_SECONDS` the same way a quick
   * PDF/image open already is, and an explicit confirmation must never be
   * blocked by an unplayed-video check. Accrual
   * resumes immediately after settling the view, so time keeps accruing
   * normally until the surface closes the modal (which callers do right away
   * on success) — that close settles as a normal, usually-zero, top-up
   * increment.
   *
   * The view side always uses the keepalive transport, same as a passive
   * background flush: the confirmation is usually the last thing a caller
   * does before closing the viewer, so it is just as exposed to the page
   * tearing down mid-request as any other flush. Unlike a passive flush, a
   * beacon failure here retries over Apollo with the same `seconds` rather
   * than a freshly recomputed one — this call already committed to reporting
   * exactly that amount back to its caller. The mark-as-read call itself is a
   * foreground, user-initiated action (not exposed to teardown the same way,
   * since it fires from a button click before the modal closes), so it goes
   * straight over Apollo.
   *
   * Resolves `null` when there is nothing to confirm: no open session, or the
   * upgrade-required latch is already set. If a passive flush's create is
   * already on the wire for this version, waits for it (and for any retry it
   * kicks off) to settle instead of bailing out from under it or racing a
   * second create. Rejects when either mutation this call is responsible for
   * fails, so the caller can surface it — unlike passive tracking, a
   * confirmation the user explicitly asked for must not fail silently.
   */
  const confirmRead = useCallback(async (): Promise<{
    id: string;
    readingDurationSeconds: number;
  } | null> => {
    const versionId = activeVersionRef.current;
    if (!versionId || upgradePlanRequired) return null;

    pauseAccrual(versionId);
    const seconds = Math.max(MIN_SECONDS, accruedSeconds(versionId));
    resumeAccrual(versionId);

    // A passive flush's create for this version may already be on the wire
    // (e.g. the tab was backgrounded and foregrounded again just before the
    // click) — possibly followed by its own beacon-failure retry. Wait out
    // the whole chain rather than the single snapshot taken here.
    const racing = inFlightByVersion.get(versionId);
    if (racing) await waitForRacingCreate(versionId, racing);

    let viewId = viewIdsByVersion.get(versionId);
    if (!viewId) {
      if (upgradePlanRequired) return null;

      const onCreated = (createdId: string | null) => {
        inFlightByVersion.delete(versionId);
        if (createdId) {
          viewIdsByVersion.set(versionId, createdId);
          creditedSecondsByVersion.set(versionId, seconds);
          touchVersion(versionId);
        }
        return createdId;
      };

      const request: Promise<string | null> = sendViewCreateBeacon(
        versionId,
        new Date(Date.now()).toISOString(),
        seconds,
      )
        .then(onCreated)
        .catch(() =>
          createOverApollo(versionId, seconds)
            .then(onCreated)
            .catch((error) => {
              inFlightByVersion.delete(versionId);
              throw error;
            }),
        );
      inFlightByVersion.set(versionId, request);

      viewId = (await request) ?? undefined;
      if (!viewId) return null;
    } else {
      const confirmedViewId = viewId;
      const credited = creditedSecondsByVersion.get(versionId) || 0;
      const owed = seconds - credited;
      if (owed > 0) {
        creditedSecondsByVersion.set(versionId, credited + owed);
        try {
          await sendViewIncrementBeacon(confirmedViewId, owed).catch(() =>
            incrementOverApollo(confirmedViewId, owed),
          );
        } catch (error) {
          creditedSecondsByVersion.set(versionId, credited);
          throw error;
        }
      }
      touchVersion(versionId);
    }

    await confirmMarkAsRead(versionId);
    return { id: viewId, readingDurationSeconds: seconds };
  }, [
    accruedSeconds,
    pauseAccrual,
    resumeAccrual,
    createOverApollo,
    incrementOverApollo,
    confirmMarkAsRead,
  ]);

  useEffect(() => {
    if (!documentationVersionId) return undefined;
    const versionId = documentationVersionId;
    const token = instanceTokenRef.current as symbol;

    activeVersionRef.current = versionId;
    // Freeze how this session is measured, while the attachment is still on
    // screen to say so.
    sessionModeRef.current.set(versionId, {
      isVideo: isVideoRef.current,
      getWatched: watchedRef.current,
    });
    touchVersion(versionId);

    if (!primaryTrackerByVersion.has(versionId)) {
      primaryTrackerByVersion.set(versionId, token);
    }
    isPrimaryRef.current = primaryTrackerByVersion.get(versionId) === token;

    if (!document.hidden) resumeAccrual(versionId);

    // Both events mean "this may be the last moment we get". They can fire in
    // succession for a single tab close; the second flush finds nothing owed.
    const endSession = () => {
      pauseAccrual(versionId);
      flush(versionId, true);
    };
    const onVisibilityChange = () => {
      if (document.hidden) endSession();
      else resumeAccrual(versionId);
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', endSession);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', endSession);
      pauseAccrual(versionId);
      flush(versionId, false);
      // Bank this session's playback before the player resets to zero, so a
      // reopen adds to the total instead of replaying from the start. Must run
      // after the flush above, which still needs the live reading. Only the
      // primary tracker contributes — see `primaryTrackerByVersion`.
      const { isVideo: sessionIsVideo, getWatched } = sessionMode(versionId);
      if (sessionIsVideo && isPrimaryRef.current) {
        const carry = watchedCarryByVersion.get(versionId) || 0;
        watchedCarryByVersion.set(versionId, carry + (getWatched?.() ?? 0));
      }
      if (primaryTrackerByVersion.get(versionId) === token) {
        primaryTrackerByVersion.delete(versionId);
      }
      activeVersionRef.current = null;
    };
  }, [documentationVersionId, flush, pauseAccrual, resumeAccrual, sessionMode]);

  return { confirmRead };
}
